import { noItx } from "../../../src/translationItx/itx";
import { C1_PDF_FLOOR_FINISH_IMAGES } from "../data/C1_floorFinishes";

const PRINT_DPI = 300;
const JPEG_QUALITY = 0.85;
const IMAGE_FETCH_TIMEOUT_MS = 15_000;
const XLINK_NAMESPACE = "http://www.w3.org/1999/xlink";

type ImagePlacement = { element: SVGImageElement; source: string; maxPixels: number };

/** Measures transformed image sizes in PDF points using an isolated SVG clone. */
function imagePlacements(svg: SVGSVGElement, width: number, height: number): ImagePlacement[] {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-100000px;top:0;visibility:hidden;pointer-events:none";
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.style.setProperty("width", `${width}px`, "important");
  clone.style.setProperty("height", `${height}px`, "important");
  host.append(clone);
  document.body.append(host);
  try {
    const originals = svg.querySelectorAll<SVGImageElement>("image");
    const copies = clone.querySelectorAll<SVGImageElement>("image");
    const placements: ImagePlacement[] = [];
    for (let index = 0; index < originals.length; index += 1) {
      const element = originals[index];
      const source = element.getAttribute("href") || element.getAttributeNS(XLINK_NAMESPACE, "href");
      if (!source) continue;
      const rect = copies[index].getBoundingClientRect();
      const edge = Math.max(rect.width, rect.height);
      // Round up to reusable size buckets so similar symbols share the same encoded image.
      const maxPixels = Math.ceil((edge * PRINT_DPI) / 72 / 128) * 128;
      placements.push({ element, source, maxPixels });
    }
    return placements;
  } finally {
    host.remove();
  }
}

/** Preserves alpha instead of flattening transparent product graphics onto a background. */
function containsTransparency(context: CanvasRenderingContext2D, width: number, height: number): boolean {
  const pixels = context.getImageData(0, 0, width, height).data;
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] !== 255) return true;
  }
  return false;
}

/** Encodes browser canvas output without retaining a temporary object URL. */
function canvasBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("PDF-Bild konnte nicht kodiert werden."))),
      type,
      JPEG_QUALITY,
    );
  });
}

/** Supplies svg2pdf with a self-contained, cacheable image URL. */
function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** svg2pdf resizes the SVG root to its image placement, but inline CSS wins over those attributes.
 * Remove only the root's fixed CSS size; viewBox and all inner geometry stay intact.
 */
function normalizeVectorImage(markup: string): string {
  if (!/<svg\b[^>]*\sstyle\s*=/i.test(markup)) return markup;
  const root = new DOMParser().parseFromString(markup, "image/svg+xml").documentElement;
  if (root.localName !== "svg") return markup;
  const svg = root as unknown as SVGSVGElement;
  if (!svg.style.getPropertyValue("width") && !svg.style.getPropertyValue("height")) return markup;
  svg.style.removeProperty("width");
  svg.style.removeProperty("height");
  return new XMLSerializer().serializeToString(svg);
}

/** Keeps embedded SVGs local and normalizes both URL-encoded and base64 sources. */
function normalizeEmbeddedVector(source: string): string {
  const separator = source.indexOf(",");
  if (separator < 0) return source;
  const header = source.slice(0, separator);
  const payload = source.slice(separator + 1);
  const markup = /;base64/i.test(header)
    ? new TextDecoder().decode(Uint8Array.from(atob(payload), character => character.charCodeAt(0)))
    : decodeURIComponent(payload);
  const normalized = normalizeVectorImage(markup);
  return normalized === markup ? source : "data:image/svg+xml;charset=utf-8," + encodeURIComponent(normalized);
}

/** Embeds vector sources with their MIME type and limits raster size for printing. */
async function prepareRaster(source: string, maxPixels: number): Promise<string> {
  if (/^data:image\/svg\+xml/i.test(source)) return normalizeEmbeddedVector(source);
  const response = await fetch(source, { signal: AbortSignal.timeout(IMAGE_FETCH_TIMEOUT_MS) });
  if (!response.ok) throw new Error("PDF-Bild konnte nicht geladen werden.");
  const original = await response.blob();
  // svg2pdf infers external image formats from URL suffixes; blob URLs have none.
  // An embedded SVG MIME type keeps contrast-adjusted symbols visible and vector based.
  if (original.type.includes("svg") || /\.svg(?:[?#]|$)/i.test(source)) {
    const markup = await original.text();
    const normalized = normalizeVectorImage(markup);
    return blobDataUrl(new Blob([normalized], { type: "image/svg+xml" }));
  }
  const bitmap = await createImageBitmap(original);
  try {
    const scale = Math.min(1, maxPixels / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(bitmap.width * scale));
    canvas.height = Math.max(1, Math.ceil(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("PDF-Bildkontext konnte nicht erstellt werden.");
    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const type = containsTransparency(context, canvas.width, canvas.height) ? "image/png" : "image/jpeg";
    const optimized = await canvasBlob(canvas, type);
    return blobDataUrl(optimized.size < original.size ? optimized : original);
  } finally {
    bitmap.close();
  }
}

/** Shares raster conversions across all pages of one PDF export. */
export class C1_PdfImageOptimizer {
  private readonly images = new Map<string, Promise<string>>();

  async optimize(svg: SVGSVGElement, width: number, height: number): Promise<void> {
    for (const placement of imagePlacements(svg, width, height)) {
      const texture = C1_PDF_FLOOR_FINISH_IMAGES.get(placement.source);
      let source = texture;
      if (!source && Number.isFinite(placement.maxPixels) && placement.maxPixels > 0) {
        const key = `${placement.source}|${placement.maxPixels}`;
        let pending = this.images.get(key);
        if (!pending) {
          pending = prepareRaster(placement.source, placement.maxPixels).catch((error) => {
            console.warn(noItx("PDF image optimization skipped; using original image."), error);
            return placement.source;
          });
          this.images.set(key, pending);
        }
        source = await pending;
      }
      if (!source) continue;
      placement.element.setAttribute("href", source);
      placement.element.removeAttributeNS(XLINK_NAMESPACE, "href");
    }
  }
}
